"use client";

import React from "react";
import Hero from "@/components/tools/Hero";
import FAQ from "@/components/tools/FAQ";
import RelatedTopics from "@/components/tools/RelatedTopics";
import NightGuardsIntro from "@/components/services/night-guards/NightGuardsIntro";
import ImpactOfBruxism from "@/components/services/night-guards/ImpactOfBruxism";
import UnderstandingBruxism from "@/components/services/night-guards/UnderstandingBruxism";
import CustomNightGuards from "@/components/services/night-guards/CustomNightGuards";
import WhyChooseSmile4Me from "@/components/services/night-guards/WhyChooseSmile4Me";
import BenefitsOfNightGuards from "@/components/services/night-guards/BenefitsOfNightGuards";
import DentalServicesAndTech from "@/components/services/night-guards/DentalServicesAndTech";
import PatientSatisfaction from "@/components/services/night-guards/PatientSatisfaction";

const nightGuardFAQs = [
  {
    question: "What is bruxism?",
    answer:
      "Bruxism is the involuntary grinding, clenching, or gnashing of teeth, occurring during the day (awake bruxism) or at night (sleep bruxism). Nighttime bruxism is more common and often unnoticed until symptoms arise. It affects all ages, and the constant pressure can damage teeth. At Smile4Me, we address bruxism to preserve your oral health and smile.",
  },
  {
    question: "What are the signs that I might be grinding my teeth?",
    answer:
      "Teeth grinding often goes unnoticed, especially during sleep. Signs include waking with a sore jaw, headaches, or earaches; tooth or gum sensitivity; worn enamel; grinding sounds noticed by others; or flattened, chipped, or fractured teeth. If you notice these, consult Smile4Me for an evaluation and treatment options.",
  },
  {
    question: "What causes nocturnal teeth grinding?",
    answer:
      "Bruxism's causes aren't always clear, but factors include stress, anxiety, tension; misaligned teeth or jaws (malocclusion); sleep disorders like sleep apnea; certain medications or drugs; caffeine and tobacco use; or genetic predisposition. At Smile4Me, we identify causes to tailor treatment and protect your oral health.",
  },
  {
    question: "How does bruxism impact my oral health?",
    answer:
      "Bruxism can cause microscopic enamel cracks, allowing bacteria to cause decay; accelerated tooth wear, loosening, or loss; and jaw muscle strain, leading to headaches or TMJ disorders. Early treatment at Smile4Me with custom night guards prevents further damage to your teeth and jaws.",
  },
  {
    question: "What are the benefits of using a custom night guard?",
    answer:
      "Custom night guards from Smile4Me reduce jaw joint strain (TMJ), protect enamel from wear, prevent sensitivity, improve sleep by reducing grinding noise, alleviate TMJ symptoms like headaches, and preserve tooth structure, minimizing future restorative needs. They're designed for comfort and optimal protection.",
  },
  {
    question: "How do I know if I need a custom night guard?",
    answer:
      "If you suspect teeth grinding, consult Smile4Me. We'll examine your teeth, gums, and jaws, evaluate bruxism signs, discuss causes, and assess grinding severity. A custom night guard may be recommended based on your needs to protect your oral health.",
  },
  {
    question: "What makes custom night guards better than store-bought guards?",
    answer:
      "Custom night guards from Smile4Me offer better fit, comfort, and protection than store-bought guards. Made from digital impressions, they match your bite, are more durable, and can be adjusted. Store-bought guards lack precision and effectiveness, making custom guards the best choice for bruxism.",
  },
  {
    question: "What materials are used to make custom night guards?",
    answer:
      "Smile4Me's custom night guards use premium, biocompatible materials: high-quality acrylic resin for durability and adjustability, a thick outer layer for protection, and a soft inner lining for comfort. Digital scanning ensures a precise fit for optimal effectiveness.",
  },
  {
    question: "How do I clean my custom night guard?",
    answer:
      "Keep your Smile4Me night guard clean by rinsing with cold water after use, brushing gently with a soft toothbrush and mild soap or denture cleaner, soaking in a cleaning solution per instructions, and storing in a dry, protective case away from sunlight.",
  },
  {
    question: "Can children benefit from wearing a night guard?",
    answer:
      "Children and teens can suffer from bruxism, and night guards protect their teeth from wear and decay. Smile4Me crafts child-sized custom guards for comfort and effectiveness. If your child grinds their teeth, contact us for an evaluation and treatment recommendations.",
  },
  {
    question: "How long will my custom night guard last?",
    answer:
      "Smile4Me's custom night guards last 3 to 10 years, depending on use, bruxism severity, and care. Factors like clenching force and bite habits affect longevity. We monitor your guard during check-ups and recommend replacements or adjustments as needed.",
  },
  {
    question: "Can I still play sports while wearing a night guard?",
    answer:
      "Custom night guards aren't suitable for sports due to choking risks if dislodged. Smile4Me offers sports mouthguards designed for athletic activities. We'll find the best solution to protect your teeth during sleep and sports for optimal safety.",
  },
  {
    question: "How can I get a custom night guard from Smile4Me?",
    answer:
      "To get a custom night guard from Smile4Me, schedule an evaluation for bruxism. We'll take digital impressions, craft your guard in our lab using premium materials, and ensure a perfect fit at a follow-up. Call (718) 400-4400 to book your consultation.",
  },
];

const NightGuards: React.FC = () => {
  return (
    <>
      <Hero
        imageSrc="/images/system_images_services_dental_banners_nightguards_banner.webp"
        imageAlt="Nightguards Banner"
        title="Night Guards"
        subtitle="Protect your teeth from bruxism with custom night guards at Smile4Me Dental Care."
        ctaText="Book Now"
        ctaLink="/contact"
      />
      <main className="flex-1 py-8 bg-gradient-to-b from-white to-gray-50">
        <div className="flex flex-col lg:flex-row mx-auto px-4 sm:px-8 md:px-12">
          <article className="flex-1 space-y-8 pr-0 lg:pr-8">
            <NightGuardsIntro />
            <ImpactOfBruxism />
            <UnderstandingBruxism />
            <CustomNightGuards />
            <WhyChooseSmile4Me />
            <BenefitsOfNightGuards />
            <DentalServicesAndTech />
            <PatientSatisfaction />
            <FAQ title="Frequently Asked Questions" faqs={nightGuardFAQs} />
          </article>
          <RelatedTopics
            topics={[
              { label: "Invisalign®", url: "/services/invisalign" },
              { label: "Sleep Apnea", url: "/services/sleep-apnea" },
              {
                label: "Snoring Appliances",
                url: "/services/snoring-appliances",
              },
              {
                label: "Sports Mouthguards",
                url: "/services/sports-mouthguards",
              },
            ]}
          />
        </div>
      </main>
    </>
  );
};

export default NightGuards;